import React, { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { predikatColor, useActiveTA } from "@/lib/academic";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Download, Printer, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function RekapNilaiPage() {
  const { active: activeTA } = useActiveTA();
  const [tahunAjaran, setTahunAjaran] = useState("all");
  const [semester, setSemester] = useState("all");
  const [jenisUjianId, setJenisUjianId] = useState("all");
  const [kelasId, setKelasId] = useState("all");
  const [mapelId, setMapelId] = useState("all");

  const [taList, setTaList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [jenisList, setJenisList] = useState([]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFilters = useCallback(async () => {
    try {
      const [ta, kel, ju, mp] = await Promise.all([
        api.get("/tahun-ajaran"),
        api.get("/kelas", { params: { limit: 200 } }),
        api.get("/jenis-ujian"),
        api.get("/mata-pelajaran"),
      ]);
      setTaList(ta.data.items || []);
      setKelasList(kel.data.items || []);
      setJenisList(ju.data.items || []);
      setMapelList(mp.data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  // default TA from active
  useEffect(() => {
    if (activeTA && tahunAjaran === "all") {
      setTahunAjaran(activeTA.tahun_ajaran);
      setSemester(activeTA.semester);
    }
  }, [activeTA, tahunAjaran]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tahunAjaran !== "all") params.tahun_ajaran = tahunAjaran;
      if (semester !== "all") params.semester = semester;
      if (jenisUjianId !== "all") params.jenis_ujian_id = jenisUjianId;
      if (kelasId !== "all") params.kelas_id = kelasId;
      if (mapelId !== "all") params.mata_pelajaran_id = mapelId;
      const { data } = await api.get("/nilai/rekap", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tahunAjaran, semester, jenisUjianId, kelasId, mapelId]);

  useEffect(() => { load(); }, [load]);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (tahunAjaran !== "all") p.set("tahun_ajaran", tahunAjaran);
    if (semester !== "all") p.set("semester", semester);
    if (jenisUjianId !== "all") p.set("jenis_ujian_id", jenisUjianId);
    if (kelasId !== "all") p.set("kelas_id", kelasId);
    if (mapelId !== "all") p.set("mata_pelajaran_id", mapelId);
    return p;
  };

  const handleExportExcel = () => {
    window.open(`${API}/nilai/rekap/export?${buildParams().toString()}`, "_blank");
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const today = new Date().toLocaleDateString("id-ID");
    const rows = items.map((r) => `
      <tr>
        <td>${r.tahun_ajaran}/${r.semester}</td>
        <td>${r.nis}</td><td>${r.santri_nama}</td>
        <td>${r.kelas_nama}</td><td>${r.mata_pelajaran_nama}</td>
        <td>${r.jenis_ujian_nama}</td>
        <td style="text-align:center;font-weight:700;">${r.nilai}</td>
        <td>${r.predikat}</td>
        <td>${r.catatan || ""}</td>
      </tr>`).join("");
    w.document.write(`
      <!doctype html><html><head><meta charset="utf-8" />
      <title>Rekap Nilai</title>
      <style>
        @page { size: A4 landscape; margin: 14mm; }
        body { font-family: 'Manrope', sans-serif; color: #0f172a; font-size: 11px; }
        h1 { font-family: 'Outfit', sans-serif; font-size: 18px; color: #047857; margin: 0; }
        .sub { color: #475569; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #e2e8f0; padding: 5px 7px; text-align: left; font-size: 10px; }
        th { background: #f1f5f9; }
        .ft { margin-top: 14px; font-size: 10px; color: #64748b; text-align: center; }
      </style></head><body>
        <h1>SiMadin — Rekap Nilai Ujian</h1>
        <div class="sub">Madrasah Al Islamiyyah Sunan Giri Surabaya • Dicetak: ${today}</div>
        <table><thead><tr>
          <th>TA/Smt</th><th>NIS</th><th>Nama</th><th>Kelas</th><th>Mata Pelajaran</th>
          <th>Jenis</th><th>Nilai</th><th>Predikat</th><th>Catatan</th>
        </tr></thead><tbody>${rows || `<tr><td colspan="9" style="text-align:center;color:#94a3b8;padding:16px;">Tidak ada data.</td></tr>`}</tbody></table>
        <div class="ft">SiMadin © 2026 — Sistem Informasi Madrasah Al Islamiyyah Sunan Giri</div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="rekap-nilai-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Nilai Ujian</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Rekap Nilai</h1>
        <p className="text-slate-500 text-sm">Lihat dan ekspor rekap nilai dengan filter lengkap.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Tahun Ajaran</Label>
            <Select value={tahunAjaran} onValueChange={setTahunAjaran}>
              <SelectTrigger className="mt-1 h-10" data-testid="rekap-nilai-tahun"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {[...new Set(taList.map((t) => t.tahun_ajaran))].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Semester</Label>
            <Select value={semester} onValueChange={setSemester}>
              <SelectTrigger className="mt-1 h-10" data-testid="rekap-nilai-semester"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="ganjil">Ganjil</SelectItem>
                <SelectItem value="genap">Genap</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Jenis Ujian</Label>
            <Select value={jenisUjianId} onValueChange={setJenisUjianId}>
              <SelectTrigger className="mt-1 h-10" data-testid="rekap-nilai-jenis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {jenisList.map((j) => <SelectItem key={j.id} value={j.id}>{j.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kelas</Label>
            <Select value={kelasId} onValueChange={setKelasId}>
              <SelectTrigger className="mt-1 h-10" data-testid="rekap-nilai-kelas"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {kelasList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Mata Pelajaran</Label>
            <Select value={mapelId} onValueChange={setMapelId}>
              <SelectTrigger className="mt-1 h-10" data-testid="rekap-nilai-mapel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {mapelList.map((m) => <SelectItem key={m.id} value={m.id}>{m.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={handleExportExcel} variant="outline" className="h-10 flex-1 gap-2" data-testid="rekap-nilai-export-excel">
              <Download className="h-4 w-4" /> Excel
            </Button>
            <Button onClick={handlePrint} variant="outline" className="h-10 flex-1 gap-2" data-testid="rekap-nilai-print">
              <Printer className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">TA/Smt</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Santri</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mata Pelajaran</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Jenis</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nilai</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Predikat</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Catatan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                  <FileSpreadsheet className="h-6 w-6 mx-auto mb-1 text-slate-300" />
                  Belum ada data nilai pada filter ini.
                </TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r.id} data-testid={`rekap-nilai-row-${r.id}`} className="hover:bg-emerald-50/30">
                  <TableCell className="text-xs text-slate-700 font-mono">{r.tahun_ajaran} <span className="capitalize">{r.semester}</span></TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{r.santri_nama}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.nis}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{r.kelas_nama}</TableCell>
                  <TableCell className="text-sm text-slate-700">{r.mata_pelajaran_nama}</TableCell>
                  <TableCell className="text-sm text-slate-700">{r.jenis_ujian_nama}</TableCell>
                  <TableCell className="text-center font-bold tabular-nums">{r.nilai}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${predikatColor(r.predikat)}`}>
                      {r.predikat}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{r.catatan || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
